/**
 * CakeAnimation — birthday cake 🎂 with 8 crackling sparkler particles
 * emitting from the candle position + a gentle bounce. Each sparkler starts
 * hot-white then fades to warm amber for a real-firework feel.
 */
export default function CakeAnimation({ size = 140 }) {
  const sparklers = new Array(14).fill(0).map((_, i) => {
    // Spread emissions across the whole 1.4s window in bursts of 3-4.
    const stagger = (i * 0.08).toFixed(2);
    const angle = ((i * 137) % 360); // Golden-angle scatter so no line
    const distance = 40 + (i % 5) * 8;
    const dx = `${Math.cos((angle * Math.PI) / 180) * distance}px`;
    const dy = `${Math.sin((angle * Math.PI) / 180) * distance - 20}px`;
    return { delay: `${stagger}s`, dx, dy, size: 5 + (i % 3) };
  });
  return (
    <div
      className="pointer-events-none"
      style={{ width: size, height: size, position: "relative", overflow: "hidden" }}
    >
      <style>{`
        @keyframes cake-bob {
          0%   { transform: translateY(20px) scale(0.5) rotate(-6deg); opacity: 0; filter: drop-shadow(0 0 0 rgba(251,191,36,0)); }
          25%  { transform: translateY(-8px) scale(1.5) rotate(4deg); opacity: 1; filter: drop-shadow(0 0 30px rgba(244,63,94,0.85)); }
          55%  { transform: translateY(0) scale(1.35) rotate(-2deg); opacity: 1; filter: drop-shadow(0 0 26px rgba(251,191,36,0.85)); }
          80%  { transform: translateY(-2px) scale(1.3) rotate(1deg); opacity: 0.95; filter: drop-shadow(0 0 22px rgba(244,63,94,0.7)); }
          100% { transform: translateY(0) scale(1) rotate(0deg); opacity: 0; filter: drop-shadow(0 0 0 rgba(251,191,36,0)); }
        }
        @keyframes cake-sparkler {
          0%   { transform: translate(0, 0) scale(0.2); opacity: 0; background: #fef9c3; box-shadow: 0 0 0 rgba(254,249,195,0); }
          15%  { transform: translate(calc(var(--dx) * 0.3), calc(var(--dy) * 0.3)) scale(1); opacity: 1; background: #ffffff; box-shadow: 0 0 12px #fef9c3, 0 0 24px #fbbf24; }
          55%  { opacity: 1; background: #fbbf24; box-shadow: 0 0 8px #f59e0b; }
          100% { transform: translate(var(--dx, 0), var(--dy, 0)) scale(0.3); opacity: 0; background: #ef4444; box-shadow: 0 0 4px #ef4444; }
        }
        @keyframes cake-flame {
          0%, 100% { transform: translateX(-50%) scale(1) skewX(0deg); opacity: 0.9; }
          25% { transform: translateX(-50%) scale(1.15, 1.25) skewX(-4deg); opacity: 1; }
          50% { transform: translateX(-50%) scale(1.05, 1.15) skewX(3deg); opacity: 0.95; }
          75% { transform: translateX(-50%) scale(1.1, 1.2) skewX(-2deg); opacity: 1; }
        }
      `}</style>

      {/* Little dancing flame above the candle */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "22%",
          width: 8,
          height: 12,
          background: "radial-gradient(ellipse at bottom, #fef3c7 0%, #fbbf24 40%, #ef4444 100%)",
          borderRadius: "50% 50% 40% 40% / 60% 60% 40% 40%",
          transform: "translateX(-50%)",
          animation: "cake-flame 0.18s ease-in-out infinite",
          boxShadow: "0 0 12px #f59e0b, 0 -4px 8px #fef3c7",
          zIndex: 2,
        }}
      />

      {/* Sparkler emissions from the candle position */}
      {sparklers.map((s, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: "50%",
            top: "30%",
            width: s.size,
            height: s.size,
            borderRadius: "50%",
            transform: "translate(-50%, -50%)",
            animation: `cake-sparkler 1.4s ease-out ${s.delay} forwards`,
            "--dx": s.dx,
            "--dy": s.dy,
            zIndex: 1,
          }}
        />
      ))}

      {/* Cake emoji */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          fontSize: size * 0.7,
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          animation: "cake-bob 1.4s cubic-bezier(.22,1,.36,1) forwards",
        }}
      >
        🎂
      </div>
    </div>
  );
}
